import dayjs from 'dayjs'
import { maxBy, minBy, startCase } from 'lodash-es'
import { FEETS_PER_METER } from '#/consts'
import { NEIGHBORHOOD_RESIDENTIAL_SUBDIVISION_LEVEL } from '#/consts/neighborhood_level'
// import { determineInsights } from '#/containers/PropertyDetail/components/Insights/Insights'
import { LOT_DETAILS_DESCRIPTIONS, LOT_DETAILS_METRICS, LOT_DETAILS_OTHER_LABELS, LOT_DETAILS_TOLERANCE, NEARBY_STATISTICS_DESCRIPTIONS, NEARBY_STATISTICS_METRICS, NEARBY_STATISTICS_OTHER_LABELS, NEARBY_STATISTICS_TOLERANCE } from '#/containers/PropertyDetail/components/Insights/consts'
import { INSIGHT_DESCRIPTIONS, INSIGHT_METRICS, INSIGHT_OTHER_LABELS, INSIGHT_TOLERANCE } from '#/containers/PropertyDetail/components/Valuations/consts'
// import { groupSchoolsbyType } from '#/hooks/useSchoolTypes'
import { getNeighborhood } from '#/utils/properties'
import { commaNumber } from './number'

type AggregationMetricRank = {
  percent: number
  type: 'SMALLEST' | 'SMALLER' | 'BIGGER' | 'BIGGEST',
}
type GetInsight =
  (
    data: TopHap.PropertyAggregateMetricData,
    min: number,
    max: number,
    metric:TopHap.InsightMetric,
    tolerance?: number,
  ) => AggregationMetricRank | undefined

export const getInsight: GetInsight = (data, min, max, metric, tolerance = 10) => {
  const { eq, gt, lt } = data.count
  const total = data.count.eq + data.count.gt + data.count.lt
  // min/max conditions for showing insight cards, allows us to only show insight when it the difference is significant
  // don't show slope insight if max-min < 5
  if (metric === 'slope' && max - min < 5) return undefined

  // don't show elevation insight if max-min < 30ft
  if (metric === 'elevation' && (max - min) * FEETS_PER_METER < 30) return undefined
  if (total >= 5 && data.max !== data.min) {
    // Biggest
    // if the one and only largest. none above
    if (gt === 0 && eq === 1) {
      return {
        percent: 100,
        type: 'BIGGEST',
        min,
        max,
        metric
      }
    }

    // Bigger
    // if more than N% are below you and show what pecerent are lower
    const ltPercent = lt * 100 / total
    if (ltPercent >= (100 - tolerance)) {
      return {
        percent: ltPercent,
        type: 'BIGGER',
        min,
        max,
        metric
      }
    }

    // Smallest
    // if the one and only smallest. none below
    if (lt === 0 && eq === 1) {
      return {
        percent: 100,
        type: 'SMALLEST',
        min,
        max,
        metric
      }
    }

    // Smaller
    // if more than N% are above you and show what percent are larger
    const gtPercent = gt * 100 / total
    if (gtPercent >= (100 - tolerance)) {
      return {
        percent: gtPercent,
        type: 'SMALLER',
        min,
        max,
        metric
      }
    }
  }
}

// function that returns the proper const value for the metric
export const getInsightDescription = (metric: TopHap.InsightMetric) => {
  if (LOT_DETAILS_METRICS.includes(metric)) {
    return {
      data: LOT_DETAILS_DESCRIPTIONS,
      index: LOT_DETAILS_METRICS.indexOf(metric),
      otherLabels: LOT_DETAILS_OTHER_LABELS,
      tolerance: LOT_DETAILS_TOLERANCE
    }
  } else if (NEARBY_STATISTICS_METRICS.includes(metric)) {
    return {
      data: NEARBY_STATISTICS_DESCRIPTIONS,
      index: NEARBY_STATISTICS_METRICS.indexOf(metric),
      tolerance: NEARBY_STATISTICS_TOLERANCE,
      otherLabels: NEARBY_STATISTICS_OTHER_LABELS
    }
  } else if (INSIGHT_METRICS.includes(metric)) {
    return {
      data: INSIGHT_DESCRIPTIONS,
      index: INSIGHT_METRICS.indexOf(metric),
      tolerance: INSIGHT_TOLERANCE,
      otherLabels: INSIGHT_OTHER_LABELS
    }
  } else {
    return {
      data: [[[]]],
      index: 0
    }
  }
}

// function takes in the name of a metric, transforms it for display on the page
export const transformMetricName = (metricName: TopHap.InsightMetric) => {
  let processedMetricName: string = metricName
  if (metricName === 'lot_size_acres') { processedMetricName = 'Lot Size' } else if (metricName === 'bedrooms') { processedMetricName = 'Bedroom Count' } else if (metricName === 'bathrooms') { processedMetricName = 'Bathroom Count' } else if (metricName === 'living_area') { processedMetricName = 'Square Footage' } else if (metricName === 'slope') { processedMetricName = 'Lot Slope' }
  return (startCase(processedMetricName))
}
export const description = (
  property: TopHap.ExtendedProperty,
  insightsData: TopHap.PropertyState['insightsData'],
  // schools?: TopHap.School[],
  withPublicRemarks?: boolean
) => {
  const neighborhood = getNeighborhood(property.zones, NEIGHBORHOOD_RESIDENTIAL_SUBDIVISION_LEVEL)
  // const insights = determineInsights([
  //   ...INSIGHT_METRICS,
  //   ...LOT_DETAILS_METRICS,
  //   ...NEARBY_STATISTICS_METRICS
  // ], insightsData)
  const insights: any[] = []
  const biggerInsights = insights.filter(insight => insight.type === 'BIGGER')
  const biggestInsights = insights.filter(insight => insight.type === 'BIGGEST')
  const smallerInsights = insights.filter(insight => insight.type === 'SMALLER')
  const smallestInsights = insights.filter(insight => insight.type === 'SMALLEST')
  const biggerPercentage = minBy(biggerInsights, e => e.percent)?.percent
  const smallerPercentage = maxBy(smallerInsights, e => e.percent)?.percent
  const hasBiggerInsights = biggerInsights.length > 0
  const hasSmallerInsights = smallerInsights.length > 0
  const hasBiggestInsights = biggestInsights.length > 0
  const hasSmallestInsights = smallestInsights.length > 0
  // const selectedSchools = groupSchoolsbyType(schools)
  const transformPropertyType = () => {
    switch (property.PropertyUse) {
      case 'Multi-family':
        return 'multi-family home'
      case 'Other':
        return 'property'
      case 'Industrial':
        return 'commercial industrial property'
      case 'Office':
        return 'commercial office property'
      case 'Retail':
        return 'commercial retail property'
      case 'Specialty':
        return 'commercial specialty property'
      case 'Mixed Use':
        return 'commercial mixed use property'
      case 'Hospitality':
        return 'commercial hospitality property'
      case 'Vacant Land / Residential':
        return 'vacant / residential '
      default:
        return (property.PropertyUse.toLowerCase().replace(' / ', '/'))
    }
  }
  const currentStatusVerbiage = property.RentFlag ? 'for rent' : ['for sale', 'on the market', 'listed'][Number(property.id) % 3]
  const addMonthSuffix = property.ListAmount ? ` for $${commaNumber(property.ListAmount)}${property.RentFlag ? '/month' : ''}` : ''
  const lastSoldPhrase = property.TransactionDate ? `, and it was last ${property.RentFlag ? 'rented' : 'sold'} in ${dayjs(property.TransactionDate).year()}` : ''
  const transactionAmountPhrase = property.TransactionAmount && property.TransactionDate ? ' for $' + commaNumber(property.TransactionAmount) : ''
  return (`${property.displayAddress ? property.displayAddress + ',' : 'This property in '} ${property.displayRegion} is a ` +
  (property.BedsCount ? `${property.BedsCount} bed, ` : '') +
  (property.BathsDecimal ? `${property.BathsDecimal} bath, ` : '') +
  (property.LivingSqft ?
    `${commaNumber(Math.round(property.LivingSqft))} ft² ` :
    '') +
  (property.PropertyUse ?
    `${property.PropertyUse !== 'Other' ? transformPropertyType().toLowerCase().replace('vacant land / residential', 'vacant / residential ').replace(' / ', '/') : ''} ${property.PropertyUse === 'Multi-family' ? 'house ' : ''} ${['Commercial', 'Other'].includes(property.PropertyUseGroup) ? 'property ' : ''}${property.PropertyUseGroup === 'Land' ? 'land ' : ''}` :
    '') + (property.YearBuilt ? `built in ${property.YearBuilt} ` : '') +
  (property.LotAcres && property.LotAcres > 0 ?
    `${property.PropertyUseGroup === 'Land' ? 'of' : 'on a'} ${Number(property.LotAcres).toFixed(3)} acre${property.PropertyUseGroup === 'Land' ? 's' : ' lot'}` :
    '') +
  (property.permitsCount ?
    ` with ${property.permitsCount} construction permit${property.permitsCount > 1 ? 's' : ''}` :
    '') +
  '.' +
  (
    (property.PropertyUse && neighborhood) ?
      ` This ${transformPropertyType()} is currently ${property.TophapStatus === 'Sold' || property.TophapStatus === 'PrivateSale' ? 'off market' :
        property.TophapStatus === 'Active' ?
          `${currentStatusVerbiage} ${addMonthSuffix}` :
          property.TophapStatus.toLowerCase()}${lastSoldPhrase}${transactionAmountPhrase}.` :
      ''
  )) +
  ((property.PropertyUse && property.estimates?.estimate && neighborhood) ?
    ` The latest TopHap estimate of the ${transformPropertyType()}'s value is $${commaNumber(property.estimates?.estimate)}.` : ''
  ) +
  (
    (property.PropertyUse && neighborhood &&
      (hasSmallerInsights || hasBiggerInsights || hasBiggestInsights || hasSmallestInsights)) ?
      ` This property is unique for ${neighborhood.name} because` : ''
  ) +
  // biggest & smallest insights
  (
    (hasBiggestInsights && hasSmallestInsights && neighborhood?.name) ?
      ` it has the 
    ${[...biggestInsights, ...smallestInsights].map((metric, i) => {
      const selectedInsight = getInsightDescription(metric.metric)
      return (`${[...biggestInsights, ...smallestInsights].length > 1 && i === [...biggestInsights, ...smallestInsights].length - 1 ? ' and the ' : ' '}
        ${selectedInsight ?
          `${selectedInsight?.data[selectedInsight.index][0][metric.type === 'BIGGEST' ? 0 : 1]}${selectedInsight.otherLabels?.[0]}` :
          ''}`)
    }).join().replace(/, ([^,]*)$/, ' $1')} of any property in the neighborhood.` :
    // biggest insights
      (
        (property.displayAddress && neighborhood?.name && hasBiggestInsights) &&
    hasBiggestInsights ?
          ` it has the 
      ${biggestInsights.map((metric, i) => {
          const selectedInsight = getInsightDescription(metric.metric)
          return (`${biggestInsights.length > 1 && i === biggestInsights.length - 1 ? ' and the ' : ' '}
          ${selectedInsight ?
              `${selectedInsight?.data[selectedInsight.index][0][1]} ${selectedInsight.otherLabels?.[0]}` :
              ''}`)
        }).join().replace(/, ([^,]*)$/, ' $1')} of any property in the neighborhood.` : ''
      ) +
  // smallest insights
  (
    (property.displayAddress && neighborhood?.name && hasSmallestInsights) &&
    hasSmallestInsights ?
      ` ${hasBiggestInsights ? `Additionally, ${property.displayAddress}` : 'it'} has the 
      ${smallestInsights.map((metric, i) => {
      const selectedInsight = getInsightDescription(metric.metric)
      return (`${smallestInsights.length > 1 && i === smallestInsights.length - 1 ? ' and the ' : ' '}
          ${selectedInsight ?
          `${selectedInsight?.data[selectedInsight.index][0][0]} ${selectedInsight.otherLabels?.[0]}` :
          ''}`)
    }).join().replace(/, ([^,]*)$/, ' $1')} of any property in the neighborhood.` : ''
  )
  ) +
  // bigger insights & smaller insights
  ((hasBiggerInsights && hasSmallerInsights && biggerPercentage && smallerPercentage) ?
    ` ${(!hasBiggerInsights && !smallestInsights || (hasBiggerInsights && hasSmallerInsights && (hasBiggestInsights || hasSmallestInsights))) ?
      `Additionally, ${property.displayAddress}` : 'it'} is in the top 
    ${100 - Math.round(biggerPercentage)}% of properties in the neighborhood for
    ${biggerInsights.map((metric, i) =>
      ((biggerInsights.length > 1 && i === biggerInsights.length - 1 ? ' and ' : ' ') + transformMetricName((metric.metric)))).join().replace(/, ([^,]*)$/, ' $1')} and in the bottom 
      ${100 - Math.round(smallerPercentage)}% for
      ${smallerInsights.map((metric, i) => ((smallerInsights.length > 1 && i === smallerInsights.length - 1 ? ' and ' : ' ') + transformMetricName((metric.metric)))).join().replace(/, ([^,]*)$/, ' $1')}.` :
    ( // bigger insights
      (
        (hasBiggerInsights && biggerPercentage) ? ` ${((!hasBiggerInsights && !smallestInsights) || (hasBiggestInsights || hasSmallestInsights)) ? `Additionally, ${property.displayAddress}` : 'it'} is in the top 
        ${100 - Math.round(biggerPercentage)}% of properties in the neighborhood for
        ${biggerInsights.map((metric, i) =>
          ((biggerInsights.length > 1 && i === biggerInsights.length - 1 ? ' and ' : ' ') + transformMetricName((metric.metric)))).join().replace(/, ([^,]*)$/, ' $1')}.` : ''
      ) +
      // smaller insights
      (
        (hasSmallerInsights && smallerPercentage) ? ` ${(hasBiggestInsights || hasSmallestInsights) ? `Additionally, ${property.displayAddress}` : ' it'} is in the bottom 
        ${100 - Math.round(smallerPercentage)}% of properties in the neighborhood for
        ${smallerInsights.map((metric, i) => ((smallerInsights.length > 1 && i === smallerInsights.length - 1 ? ' and ' : ' ') + transformMetricName((metric.metric)))).join().replace(/, ([^,]*)$/, ' $1')}.` : ''
      ))
  ) + (
    (property.displayAddress) ?
      property.zones.filter(zone => zone.type === 'school-district')[0]?.name ?
        // ` ${property.displayAddress} is within the ${property.zones.filter(zone => zone.type === 'school-district')[0]?.name}${(selectedSchools.length > 0) ?
        //   ` with nearby schools including ${selectedSchools[0].map(school => ` ${school.school_nm} (${school.school_rating ?? 'No Rating'})`)}` : ''}.` :
        ` ${property.displayAddress} is within the ${property.zones.filter(zone => zone.type === 'school-district')[0]?.name}${(property.schools.public != null) ?
          ` with nearby schools including ${property.schools.public.map(school => ` ${school.school_nm} (${school.school_rating ?? 'No Rating'})`)}` : ''}.` :
        '' :
      '') +
      (withPublicRemarks && property.PublicRemarks ?
        ` ${property.PublicRemarks.substring(0, 66) + '...'}` :
        '')
}

export const property2Place = (property: TopHap.Property) => ({
  id: property.id,
  place_type: ['address'],
  place_name: `${property.displayAddress}, ${property.displayRegion}`,
  center: property.location
} as TopHap.Place)
